// DEPRECATED: Parts of this file have been replaced by `src/rxdb/replication-v2/BorerOperatorChangeFeeds`
// *** DO NOT MODIFY *** without applying changes to the replacement.
// TODO: Clean this up https://nutrien.atlassian.net/browse/MDP-8188
import { Dayjs } from 'dayjs';
import { print } from 'graphql';
import gql from 'graphql-tag';
import type { MigrationStrategies, RxCollection, RxDocument, RxJsonSchema } from 'rxdb';

import { BaseEntity } from '../../models/BaseEntity';
import RxdbCollectionName from '../rxdbCollectionName';
import { generatePushQuery } from '../rxdbUtilityFunctions';

// Note: This feed contains pending requests until they are returned in the BorerOperatorStateFeed
// At that point, these documents have been encoded and can be removed from this collection.
export interface BorerOperatorChangeState {
  borerStateTypeId: string;
  startTime: string;
  endTime: string | null;
  comment: string;
  borerShiftAdvanceId: string | null;
  originalId: string;
  failedSync: boolean | null;
  isGeneratedState: boolean | null;
  cuttingTypeId: string | null;
  cuttingMethodId: string | null;
}

export interface BorerOperatorChange extends BaseEntity {
  borerStateId: string;
  states: BorerOperatorChangeState[];
  createdOn: string;
  borerEquipmentId: string;
  showInSchedulerView: boolean;
  failedSync: boolean | null;
  isGeneratedState: boolean | null;
}

// Data interface used in the edit drawer
export interface TempBorerOperatorChangeState {
  startTime?: Dayjs;
  endTime?: Dayjs;
  borerStateTypeId: string;
  comment: string;
  borerShiftAdvanceId?: string;
  existingTempStateId?: string;
  failedSync: boolean | null;
  id: string;
  cuttingTypeId: string | null;
  cuttingMethodId: string | null;
  // Since RHF replaces ID
  originalId: string;
  isGeneratedState: boolean | null;
}

export interface TempBorerOperatorChange {
  borerStateId: string;
  states: TempBorerOperatorChangeState[];
  createdOn: string;
  borerEquipmentId: string;
  showInSchedulerView: boolean;
  failedSync: boolean | null;
}

export type BorerOperatorChangeCollection = RxCollection<BorerOperatorChange> | null;
export type BorerOperatorChangeDocument = RxDocument<BorerOperatorChange>;

export const borerOperatorChangeSchema: RxJsonSchema<BorerOperatorChange> = {
  type: 'object',
  version: 9,
  description: 'describes an BorerOperatorChange object',
  primaryKey: 'id',
  properties: {
    id: { type: 'string', maxLength: 36 },
    updatedAt: { type: 'number' },
    isDeleted: { type: 'boolean' },
    version: { type: 'number' },
    borerStateId: {
      type: 'string',
      maxLength: 36,
    },
    createdOn: { type: 'string' },
    states: {
      type: 'array',
      uniqueItems: true,
      items: {
        type: 'object',
        properties: {
          borerStateTypeId: { type: 'string', ref: RxdbCollectionName.BORER_STATE_TYPE },
          borerShiftAdvanceId: {
            type: ['string', 'null'],
            ref: RxdbCollectionName.BORER_SHIFT_ADVANCE,
          },
          startTime: { type: 'string' },
          endTime: { type: ['string', 'null'] },
          comment: { type: 'string' },
          originalId: { type: ['string', 'null'], maxLength: 36 },
          cuttingTypeId: { type: ['string', 'null'], maxLength: 36 },
          cuttingMethodId: { type: ['string', 'null'], maxLength: 36 },
        },
      },
    },
    failedSync: { type: ['boolean', 'null'] },
    borerEquipmentId: { type: 'string' },
    showInSchedulerView: { type: 'boolean' },
    isGeneratedState: { type: ['boolean', 'null'] },
  },
};

export const borerOperatorChangeMigrationStrategies: MigrationStrategies = {
  1: (oldDoc: BorerOperatorChangeDocument) => {
    return {
      ...oldDoc,
      borerShiftAdvanceId: null,
    };
  },
  2: (oldDoc: BorerOperatorChangeDocument) => oldDoc,
  3: (oldDoc: BorerOperatorChangeDocument) => oldDoc,
  4: (oldDoc: BorerOperatorChangeDocument) => ({ ...oldDoc, showInSchedulerView: true }),
  5: (oldDoc: BorerOperatorChangeDocument) => oldDoc,
  6: (oldDoc: BorerOperatorChangeDocument) => oldDoc,
  7: (oldDoc: BorerOperatorChangeDocument) => ({ ...oldDoc, isGeneratedState: false }),
  8: (oldDoc: BorerOperatorChangeDocument) => oldDoc,
  9: (oldDoc: BorerOperatorChangeDocument) => ({
    ...oldDoc,
    states: oldDoc.states.map(state => ({ ...state, cuttingTypeId: null, cuttingMethodId: null })),
  }),
};

const submitBorerOperatorStateRequestMutation = print(gql`
  mutation submitBorerOperatorStateRequest($input: SubmitBorerOperatorStateRequestInput!) {
    submitBorerOperatorStateRequest(input: $input) {
      borerEquipmentId
      borerStateId
      id
      status
    }
  }
`);

type BorerOperatorStateRequestInput = {
  borerStateTypeId: string;
  startTime: string;
  endTime: string | null;
  comment: string | null;
  borerShiftAdvanceId: string | null;
  cuttingTypeId: string | null;
  cuttingMethodId: string | null;
};

type SubmitBorerOperatorStateRequestInput = {
  id: string;
  borerStateId: string;
  borerEquipmentId: string;
  states: BorerOperatorStateRequestInput[];
  createdOn: string;
};

const mapTempBorerOperatorChangeToInput = (
  doc: TempBorerOperatorChange,
): SubmitBorerOperatorStateRequestInput => {
  const remappedStatesWithoutId: Array<BorerOperatorStateRequestInput> =
    doc?.states?.map(state => ({
      borerStateTypeId: state.borerStateTypeId,
      startTime: state.startTime,
      endTime: state.endTime,
      comment: state.comment,
      borerShiftAdvanceId: state.borerShiftAdvanceId,
      cuttingTypeId: state.cuttingTypeId,
      cuttingMethodId: state.cuttingMethodId,
    })) || [];

  return {
    id: doc.id,
    borerStateId: doc.borerStateId,
    borerEquipmentId: doc.borerEquipmentId,
    states: remappedStatesWithoutId,
    createdOn: doc.createdOn,
  };
};

export const borerOperatorChangePushQueryBuilder = generatePushQuery(
  submitBorerOperatorStateRequestMutation,
  (doc: TempBorerOperatorChange) => {
    const input = mapTempBorerOperatorChangeToInput(doc);
    return {
      input,
    };
  },
);

export const borerOperatorChangePushModifier = (
  doc: TempBorerOperatorChange,
): TempBorerOperatorChange | null => {
  // Another local property - if we toggle one of the states to failed,
  // don't send a change request to BE
  if (doc.failedSync || doc.showInSchedulerView === false) return null;
  return doc;
};
